import { useMemo } from "react";

import { type Collection } from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import getCollectionSortOptionsQuery from "@graphql/get-collection-sort-options.graphql";
import { storefrontAPIFetcher } from "@react/hooks/fetchers";
import { DEFAULT_COLLECTION_SORT_OPTIONS } from "@react/sections/Collection/CollectionFilters/constants";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import { STOREFRONT_API_URL } from "@tablescape/constants";
import {
  type SortOption,
  type CollectionSortOption,
  type CollectionSectionSettings,
} from "@tablescape/state";

type CollectionSortOptionsConnection = {
  data: {
    collection: {
      sortOptions: {
        references: {
          nodes: CollectionSortOption[];
        };
      };
    };
  };
};

export function useCollectionSortOptions(
  collectionId: Collection["id"] | undefined
) {
  // TODO: Support localization for the filter/sort features
  // const settings = useSectionSettings<Partial<CollectionSectionSettings>>();
  // const country = settings?.meta?.localization?.country;
  const {
    meta: {
      localization: { country },
    },
  } = useSectionSettings<CollectionSectionSettings>();

  const { data } = useSWR<CollectionSortOptionsConnection>(
    collectionId && country
      ? [
          STOREFRONT_API_URL,
          getCollectionSortOptionsQuery,
          { collectionId, country },
        ]
      : null,
    storefrontAPIFetcher
  );

  const sortOptions = useMemo(() => {
    if (!data) return [];

    const nodes = data?.data?.collection?.sortOptions?.references?.nodes ?? [];

    const formattedOptions = nodes
      .map<SortOption | undefined>((node) => {
        const name = node?.label?.value ?? "";
        const sortKey = node?.sortKey?.value;
        const reverseStr = node?.reverse?.value;

        if (!name || !sortKey) return undefined;

        const reverse = String(reverseStr).toLowerCase() === "true";
        return { name, value: { reverse, sortKey } };
      })
      .filter((v): v is SortOption => v !== undefined);

    return formattedOptions.length > 0
      ? formattedOptions
      : DEFAULT_COLLECTION_SORT_OPTIONS;
  }, [data]);

  return {
    sortOptions,
  };
}
